import React, { useState, useEffect, useRef, useCallback } from "react";
import { Mascot } from "./Mascot";
import { Send, X, Sparkles, Check, Loader2 } from "lucide-react";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

// Render a single message body with light markdown (bold/italic only)
function MarkdownBody({ text }) {
  const source = String(text || "");

  const renderInline = (line, lineIndex) => {
    const codeParts = line.split(/(`[^`]+`)/g);
    return codeParts.flatMap((part, partIdx) => {
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code key={`code-${lineIndex}-${partIdx}`} className="px-1 py-0.5 bg-slate-100 rounded text-xs">
            {part.slice(1, -1)}
          </code>
        );
      }
      const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
      return boldParts.flatMap((boldPart, boldIdx) => {
        if (boldPart.startsWith("**") && boldPart.endsWith("**")) {
          return <strong key={`strong-${lineIndex}-${partIdx}-${boldIdx}`}>{boldPart.slice(2, -2)}</strong>;
        }
        const italicParts = boldPart.split(/(_[^_]+_)/g);
        return italicParts.map((italicPart, italicIdx) => {
          if (italicPart.startsWith("_") && italicPart.endsWith("_")) {
            return <em key={`em-${lineIndex}-${partIdx}-${boldIdx}-${italicIdx}`}>{italicPart.slice(1, -1)}</em>;
          }
          return <React.Fragment key={`txt-${lineIndex}-${partIdx}-${boldIdx}-${italicIdx}`}>{italicPart}</React.Fragment>;
        });
      });
    });
  };

  const lines = source.split("\n");
  return (
    <span>
      {lines.map((line, idx) => (
        <React.Fragment key={`line-${idx}`}>
          {renderInline(line, idx)}
          {idx < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </span>
  );
}

// In-chat form for the bot's structured prompts
function PromptForm({ prompt, onSubmit, onCancel, onSkip, busy }) {
  const [text, setText] = useState("");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  if (prompt.input_type === "confirm") {
    return (
      <div className="flex gap-2 mt-2 flex-wrap">
        <button
          onClick={() => onSubmit({ value: "yes" })}
          disabled={busy}
          className="text-xs bg-mg-green text-white font-semibold rounded-full px-4 py-1.5 hover:opacity-90 flex items-center gap-1"
          data-testid="prompt-confirm-yes"
          style={{ backgroundColor: "#66C32C" }}
        >
          <Check className="w-3.5 h-3.5" /> Yes, save it
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          className="text-xs bg-white border border-slate-200 text-mg-secondary rounded-full px-4 py-1.5 hover:border-mg-medical"
          data-testid="prompt-confirm-cancel"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (prompt.input_type === "choice") {
    const opts = prompt.options || [];
    const filtered = query
      ? opts.filter((o) => (o.label || "").toLowerCase().includes(query.toLowerCase()) || (o.hint || "").toLowerCase().includes(query.toLowerCase()))
      : opts;
    return (
      <div className="mt-2 space-y-2">
        {opts.length > 6 && (
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={prompt.placeholder || "Type to filter…"}
            className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg focus:border-mg-medical focus:outline-none"
            data-testid="prompt-choice-filter"
          />
        )}
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
          {filtered.map((o) => (
            <button
              key={o.value}
              onClick={() => { setSelected(o.value); onSubmit({ value: o.value }); }}
              disabled={busy}
              className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                selected === o.value
                  ? "bg-blue-50 border-mg-medical text-mg-medical font-semibold"
                  : "bg-white border-slate-200 hover:border-mg-medical hover:bg-slate-50"
              }`}
              data-testid={`prompt-choice-${o.value}`}
            >
              <div className="font-medium text-mg-navy">{o.label}</div>
              {o.hint && <div className="text-[10px] text-mg-muted mt-0.5">{o.hint}</div>}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-mg-muted italic text-center py-3">No matching options.</div>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          {prompt.allow_skip && (
            <button onClick={onSkip} disabled={busy} className="text-xs text-mg-secondary px-3 py-1 hover:text-mg-navy" data-testid="prompt-skip">Skip</button>
          )}
          <button onClick={onCancel} disabled={busy} className="text-xs text-mg-secondary px-3 py-1 hover:text-mg-navy ml-auto" data-testid="prompt-cancel-flow">Cancel</button>
        </div>
      </div>
    );
  }

  // text / default
  return (
    <div className="mt-2 flex gap-2">
      <input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && text.trim() && onSubmit({ text: text.trim() })}
        placeholder={prompt.placeholder || "Type here…"}
        disabled={busy}
        className="flex-1 text-sm px-3 py-2 border border-slate-200 rounded-xl bg-white focus:border-mg-medical focus:outline-none"
        data-testid="prompt-text-input"
      />
      <button
        onClick={() => text.trim() && onSubmit({ text: text.trim() })}
        disabled={busy || !text.trim()}
        className="text-xs mg-gradient-primary text-white font-semibold px-3 rounded-xl disabled:opacity-50"
        data-testid="prompt-text-submit"
      >
        OK
      </button>
    </div>
  );
}

export function AssistantWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {from, text, prompt?, result?}
  const [suggestions, setSuggestions] = useState([]);
  const [greeting, setGreeting] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [badge, setBadge] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [activePrompt, setActivePrompt] = useState(null); // currently-awaiting form
  const [aiStatus, setAiStatus] = useState({ allowed: true, llm_allowed: true, rule_based_allowed: true });
  const endRef = useRef(null);
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    api.get("/assistant/suggestions").then((r) => {
      setSuggestions(r.data.suggestions || []);
      setGreeting(r.data.greeting || "");
      setDisclaimer(r.data.disclaimer || "");
      setBadge(r.data.badge || null);
    }).catch(() => {});
    api.get("/assistant/status").then((r) => setAiStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && messages.length === 0 && greeting) {
      setMessages([{ from: "bot", text: greeting }]);
    }
  }, [open, messages.length, greeting]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, activePrompt]);

  const sendToBackend = useCallback(async ({ message, action, payload }) => {
    if (message) {
      setMessages((m) => [...m, { from: "user", text: message }]);
    } else if (payload?.value) {
      // Echo the user's choice for clarity
      const label = activePrompt?.options?.find((o) => o.value === payload.value)?.label;
      if (label) setMessages((m) => [...m, { from: "user", text: label }]);
    }
    setActivePrompt(null);
    setLoading(true);
    try {
      const r = await api.post("/assistant/chat", {
        session_id: sessionId,
        message: message || payload?.text || "",
        action,
        payload,
      });
      const d = r.data;
      if (d.session_id) setSessionId(d.session_id);
      if (d.type === "prompt") {
        setMessages((m) => [...m, { from: "bot", text: d.content, prompt: d }]);
        setActivePrompt(d);
      } else if (d.type === "result") {
        setMessages((m) => [...m, { from: "bot", text: d.content, result: d }]);
        toast.success("Done — created via assistant");
      } else {
        setMessages((m) => [...m, { from: "bot", text: d.content }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { from: "bot", text: "Sorry, I couldn't process that. Please try again." }]);
    } finally {
      setLoading(false);
    }
  }, [activePrompt, sessionId]);

  // Auto-open intro for clinic admins with incomplete setup
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!user?.user_id || !greeting || !badge) return;
    if (badge.kind !== "setup") return;
    const flagKey = `mg_asst_intro_${user.user_id}`;
    if (sessionStorage.getItem(flagKey) === "1") return;
    autoOpenedRef.current = true;
    sessionStorage.setItem(flagKey, "1");
    setTimeout(async () => {
      setOpen(true);
      setTimeout(async () => {
        await sendToBackend({ message: "What's next in my setup?" });
      }, 900);
    }, 1200);
  }, [user?.user_id, greeting, badge, sendToBackend]);

  const handleQuickAsk = (q) => sendToBackend({ message: q });

  const sendTyped = () => {
    if (!text.trim()) return;
    sendToBackend({ message: text });
    setText("");
  };

  const submitPrompt = (data) => sendToBackend({ payload: data });
  const cancelFlow = () => sendToBackend({ action: "cancel" });
  const skipPrompt = () => sendToBackend({ action: "skip" });

  return (
    <>
      {!aiStatus.allowed ? null : !open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-20 h-20 rounded-full bg-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform mg-pulse-primary z-50 border border-slate-100 overflow-hidden p-1"
          data-testid="assistant-open-btn"
          title="Ask MediGenie"
        >
          <Mascot size={70} variant="main" />
          {badge && (
            <span
              className="absolute -top-1 -right-1 min-w-[24px] h-[24px] px-1.5 text-white text-[11px] font-extrabold rounded-full flex items-center justify-center border-2 border-white shadow-md"
              style={{ backgroundImage: "linear-gradient(135deg, #2563EB 0%, #14B8A6 100%)" }}
              data-testid="assistant-badge"
              title={badge.kind === "setup" ? "Clinic setup progress" : ""}
            >
              {badge.label}
            </span>
          )}
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 w-[400px] max-w-[92vw] h-[600px] max-h-[82vh] bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col z-50 mg-fade-in" data-testid="assistant-panel">
          <div className="mg-gradient-primary p-4 text-white flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-md overflow-hidden shrink-0">
              <Mascot size={46} variant="avatar" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-base flex items-center gap-1.5">
                MediGenie <Sparkles className="w-4 h-4" />
                {aiStatus.llm_allowed && (
                  <span className="text-[9px] uppercase bg-white/20 px-1.5 py-0.5 rounded font-bold tracking-wider">AI</span>
                )}
              </div>
              <div className="text-xs opacity-90">
                {aiStatus.llm_allowed ? "Powered by OpenAI · grounded in your clinic data" : "Guided workflows (rule-based)"}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-lg" data-testid="assistant-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center text-center pt-6 pb-4">
                <Mascot size={120} variant="main" className="mg-float" />
                <div className="mt-3 text-sm font-semibold text-mg-navy">Hi there ✨</div>
                <div className="text-xs text-mg-secondary mt-1">Ask me anything, or say &ldquo;create a new role&rdquo;.</div>
              </div>
            )}
            {messages.map((m, i) => {
              const isPromptLatest = m.prompt && i === messages.length - 1 && activePrompt;
              return (
                <div key={i} className={`flex items-end gap-2 ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  {m.from === "bot" && (
                    <div className="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                      <Mascot size={26} variant="avatar" />
                    </div>
                  )}
                  <div
                    className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.from === "user"
                        ? "text-white rounded-br-sm"
                        : m.result
                          ? "bg-green-50 border border-green-200 text-mg-navy rounded-bl-sm"
                          : "bg-white text-mg-navy border border-slate-100 rounded-bl-sm"
                    }`}
                    style={m.from === "user" ? { backgroundImage: "linear-gradient(135deg, #2090CF 0%, #00A2E8 100%)" } : {}}
                    data-testid={`assistant-msg-${m.from}`}
                  >
                    <MarkdownBody text={m.text} />
                    {isPromptLatest && (
                      <PromptForm
                        prompt={m.prompt}
                        onSubmit={submitPrompt}
                        onCancel={cancelFlow}
                        onSkip={skipPrompt}
                        busy={loading}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex items-end gap-2 justify-start">
                <div className="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                  <Mascot size={26} variant="avatar" />
                </div>
                <div className="bg-white border border-slate-100 px-3 py-2 rounded-2xl text-xs text-mg-secondary inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> MediGenie is thinking…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {messages.length <= 1 && !activePrompt && suggestions.length > 0 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 bg-slate-50">
              {suggestions.slice(0, 4).map((s, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickAsk(s)}
                  className="text-xs bg-white border border-slate-200 hover:border-mg-medical hover:text-mg-medical rounded-full px-3 py-1.5 transition-colors"
                  data-testid={`assistant-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {!activePrompt && (
            <div className="border-t border-slate-100 p-2 bg-white">
              <div className="flex items-center gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendTyped()}
                  placeholder='Ask anything… try "add a new user"'
                  disabled={loading}
                  className="flex-1 px-3 py-2 bg-slate-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-mg-medical/30"
                  data-testid="assistant-input"
                />
                <button
                  onClick={sendTyped}
                  disabled={loading}
                  className="w-10 h-10 mg-gradient-primary rounded-xl flex items-center justify-center text-white disabled:opacity-50"
                  data-testid="assistant-send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              {disclaimer && <div className="text-[10px] text-slate-400 mt-1 text-center">{disclaimer}</div>}
            </div>
          )}
          {activePrompt && (
            <div className="border-t border-slate-100 p-2 bg-amber-50/50 text-[10px] text-amber-800 text-center">
              MediGenie is guiding you through <strong className="capitalize">{activePrompt.flow?.replace(/_/g, " ")}</strong> · step <strong>{activePrompt.step}</strong>
            </div>
          )}
        </div>
      )}
    </>
  );
}
